import { Plus } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { ProcessBadge } from '@/components/sv/process-badge'
import { Small } from '@/components/sv/typographies'
import { Separator } from '@/components/ui/separator'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { isPresent } from '@/lib/utils'
import type { CreateAppFormValues } from '@/routes/apps/create-app/form/schema'
import type {
  ResourceTypesQuery,
  ResourceTypesQueryVariables,
} from '@/routes/apps/create-app/form/sections/overview/estimation.types.gen'
import type { ReactElement } from 'react'

export const RESOURCE_TYPES = gql`
  query ResourceTypes($idCompany: String!) {
    buildResourceTypes {
      id
      cpuLimit
      memoryLimit
      name
      price {
        unitNetPrice
      }
    }
    company(id: $idCompany) {
      id
      sevallaBilling {
        id
        taxPercent
      }
      bandwidthResourceType {
        id
        name
        price {
          unitNetPrice
        }
      }
      processResourceTypes {
        id
        name
        category
        cpuLimit
        memoryLimit
        price {
          unitNetPrice
          estimatedMonthlyPrice
        }
      }
      persistenceResourceTypes {
        id
        storageLimit
        price {
          unitNetPrice
          estimatedMonthlyPrice
        }
      }
    }
  }
`

export const Estimation = (): ReactElement => {
  const form = useFormContext<CreateAppFormValues>()
  const idProcessResourceType = form.watch('idProcessResourceType')
  const process = {
    type: 'web',
    displayName: 'Web process',
    idProcessResourceType,
    instanceCount: 1,
    idPersistenceResourceType: '',
    persistentStorageMountPath: '',
  }

  const { idCompany } = useIdCompany()
  const { data } = useSuspenseQuery<
    ResourceTypesQuery,
    ResourceTypesQueryVariables
  >(RESOURCE_TYPES, {
    variables: { idCompany },
  })

  const processResourceType = data?.company?.processResourceTypes?.find(
    (resourceType) => resourceType?.id === process?.idProcessResourceType
  )

  const buildResourceType = data?.buildResourceTypes?.find((resourceType) =>
    resourceType?.name?.includes('standard')
  )
  const taxPercent = data?.company?.sevallaBilling?.taxPercent ?? 0
  const persistenceResourceTypes = [
    data?.company?.persistenceResourceTypes?.find(
      (resourceType) => resourceType.id === process?.idPersistenceResourceType
    ),
  ].filter(isPresent)

  const processPrice = processResourceType?.price?.estimatedMonthlyPrice
    ? processResourceType?.price?.estimatedMonthlyPrice * (taxPercent / 100 + 1)
    : 'N/A'
  const buildPrice = buildResourceType?.price?.unitNetPrice
    ? (
        buildResourceType?.price?.unitNetPrice *
        60 *
        (taxPercent / 100 + 1)
      ).toFixed(4)
    : 'N/A'
  const bandwidthPrice = (0.1 * (taxPercent / 100 + 1)).toFixed(3)

  const content = (
    <div className="flex flex-col gap-3">
      {processResourceType ? (
        <div className="flex items-center justify-between">
          <Small className="flex items-center gap-1.5">
            <ProcessBadge
              category={processResourceType.category}
              name={processResourceType.name}
            />
            process
          </Small>
          <Small>${processPrice} / month</Small>
        </div>
      ) : null}
      {processResourceType || persistenceResourceTypes.length ? (
        <Separator />
      ) : null}
      <div className="flex items-center justify-between">
        <Small className="flex items-center gap-1">
          <Plus className="size-3" /> Build
        </Small>
        <Small>${buildPrice} / minute</Small>
      </div>
      <div className="flex items-center justify-between">
        <Small className="flex items-center gap-1">
          <Plus className="size-3" /> Bandwidth
        </Small>
        <Small>${bandwidthPrice} / GB</Small>
      </div>
    </div>
  )

  return content
}
